import { useState } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import { useSearchIcd10Codes } from "@/clinical-codes/useSearchIcd10Codes";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useToggleIcd10Comorbidity } from "@/clinical-codes/useToggleIcd10Comorbidity";

const Icd10CodesList = () => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const { codes, isLoading, error, fetchNextPage, hasNextPage } = useSearchIcd10Codes(debouncedSearch);
  const toggleComorbidity = useToggleIcd10Comorbidity();

  return (
    <div className="space-y-4">
      <div>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search ICD-10 codes or descriptions..."
          aria-label="Search ICD-10 codes"
          className="w-full max-w-md"
        />
      </div>
      {isLoading && <div>Loading...</div>}
      {error && <div className="text-destructive">Error loading codes. Please try again.</div>}
      {!isLoading && codes && codes.length === 0 && <div>No results found.</div>}
      {!isLoading && codes && codes.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border">
            <thead>
              <tr className="bg-muted">
                <th className="px-2 py-1 text-left">Code</th>
                <th className="px-2 py-1 text-left">Description</th>
                <th className="px-2 py-1 text-left">Mandatory Co-morbidity</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((code) => (
                <tr key={code.code} className="border-t">
                  <td className="px-2 py-1 font-mono whitespace-nowrap">{code.code}</td>
                  <td className="px-2 py-1">{code.description}</td>
                  <td className="px-2 py-1">
                    <div className="flex items-center gap-1">
                      {code.isMandatoryComorbidity ? (
                        <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                          No
                        </span>
                      )}
                      <Switch
                        checked={code.isMandatoryComorbidity}
                        disabled={toggleComorbidity.isPending}
                        onCheckedChange={(checked) =>
                          toggleComorbidity.mutate({
                            icd10CodeId: code.id,
                            shouldBeMandatory: checked,
                          })
                        }
                        aria-label={`Toggle mandatory co-morbidity for ${code.code}`}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hasNextPage && (
        <div>
          <Button onClick={fetchNextPage} disabled={isLoading} size="sm">
            Load More
          </Button>
        </div>
      )}
    </div>
  );
};

export default Icd10CodesList;
